import { useTranslation } from '../../../hooks/useTranslations';
import { useEffect, useId, useRef, useState } from 'react';
import classes from './MultiSelectFilter.module.css';

export interface MultiSelectFilterOption<T extends string | number> {
  value: T;
  label: string;
  description?: string;
}

interface Props<T extends string | number> {
  label: string;
  placeholder: string;
  ariaLabel: string;
  options: MultiSelectFilterOption<T>[];
  selectedValues: T[];
  onChange: (values: T[]) => void;
  disabled?: boolean;
}

export const MultiSelectFilter = <T extends string | number>({
  label,
  placeholder,
  ariaLabel,
  options,
  selectedValues,
  onChange,
  disabled = false,
}: Props<T>) => {
  const { translations } = useTranslation();
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const menuId = useId();
  // Preserve access to selected values if the available options change after a refresh.
  const availableOptions: MultiSelectFilterOption<T>[] = [
    ...options,
    ...selectedValues
      .filter((value) => !options.some((option) => option.value === value))
      .map((value) => ({ value, label: String(value) })),
  ];

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  useEffect(() => {
    if (!open) return;
    const closeOutside = (event: MouseEvent | FocusEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', closeOutside);
    document.addEventListener('focusin', closeOutside);
    return () => {
      document.removeEventListener('mousedown', closeOutside);
      document.removeEventListener('focusin', closeOutside);
    };
  }, [open]);

  const toggleValue = (value: T) => {
    onChange(
      selectedValues.includes(value)
        ? selectedValues.filter((selectedValue) => selectedValue !== value)
        : [...selectedValues, value]
    );
  };

  return (
    <div
      ref={container}
      className={classes.container}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && open) {
          event.stopPropagation();
          setOpen(false);
          trigger.current?.focus();
        }
      }}
    >
      <button
        ref={trigger}
        type="button"
        className={classes.trigger}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((previous) => !previous)}
      >
        {selectedValues.length ? `${label} (${selectedValues.length})` : placeholder}
      </button>
      {open && !disabled && (
        <div id={menuId} className={classes.menu} role="group" aria-label={label}>
          {availableOptions.map(({ value, label: optionLabel, description }) => (
            <label key={value} className={classes.option}>
              <input
                type="checkbox"
                checked={selectedValues.includes(value)}
                onChange={() => toggleValue(value)}
              />
              <span>
                <span>{optionLabel}</span>
                {description && <small>{description}</small>}
              </span>
            </label>
          ))}
          {!availableOptions.length && (
            <div className={classes.empty}>{translations('task_board.no_options_available')}</div>
          )}
          {selectedValues.length > 0 && (
            <button type="button" className={classes.clear} onClick={() => onChange([])}>
              {translations('task_board.clear_selection')}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
